import { NativeStackHeaderProps } from '@react-navigation/native-stack';
import React, { useEffect } from 'react';
import useMedicalProfile from '../../../hooks/api/useMedicalProfile';
import { LoadableAvatar, LoadingAvatarPlaceholder, getFallbackFromName } from '../Avatar';
import urls from '../../../utils/urls';
import getName from '../../../utils/getName';
import SkeletonLoadingContainer from '../SkeletonLoadingContainer';
import Text from '../Text';
import BaseHeader from './BaseHeader';
import { getHeaderTitle } from '@react-navigation/elements';
import useNotes from '../../../hooks/api/useNotes';
import { useNestedNavigation } from '../../../utils/appNavigationNames';
import AIChatButton from '../AIChatButton';

export type ProfileHeaderOwnProps = {};

export type ProfileHeaderProps = ProfileHeaderOwnProps & NativeStackHeaderProps;

type InitializingProfileHeaderProps = {
    isInitializing: true;
    profileId: null;
    omitChatButton?: boolean;
};

type ProfileHeaderBaseProps = {
    profileId: string;
    isInitializing?: false;
    omitChatButton?: boolean;
};

type MaybeInitializingProfileHeaderProps = InitializingProfileHeaderProps | ProfileHeaderBaseProps;

const useRedirectOnInvalidParams = (isInvalid: boolean) => {
    const { nestedActivityNavigate } = useNestedNavigation();
    useEffect(() => {
        if (isInvalid) {
            nestedActivityNavigate('root', undefined);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isInvalid]);
};

const HEADER_TITLE_FALLBACK = 'fall-back-header-title';

const ProfileHeaderBase: React.FC<MaybeInitializingProfileHeaderProps & NativeStackHeaderProps> = (props) => {
    const getTitle = () => getHeaderTitle(props.options, HEADER_TITLE_FALLBACK);
    const profile = useMedicalProfile({
        medicalProfileId: props.profileId,
    });

    const getCenterContent = () => {
        /*
            If an explicit title is set in the route, show that (base header manages this)
            Else there was no name configured or set and we want to show the loaded profile name
         */
        if (getTitle()?.toLowerCase() !== HEADER_TITLE_FALLBACK) {
            // react navigation auto-capitalizes the route name
            return undefined;
        } else {
            return (
                <SkeletonLoadingContainer isLoading={profile.isLoadingProfile || !!props.isInitializing}>
                    <Text variant="h4" alignText="center">
                        {profile.profile ? getName(profile.profile.contactDetails) : 'Loading...'}
                    </Text>
                </SkeletonLoadingContainer>
            );
        }
    };

    return (
        <BaseHeader
            {...props}
            height={60}
            hideBackButton={false}
            leftContent={
                props.isInitializing ? (
                    <LoadingAvatarPlaceholder size="sm" />
                ) : (
                    <LoadableAvatar
                        avatarUrl={urls.api.medicalProfiles.avatar(props.profileId)}
                        size="sm"
                        fallbackName={
                            profile.profile ? getFallbackFromName(getName(profile.profile.contactDetails)) : ''
                        }
                    />
                )
            }
            centerContent={getCenterContent()}
            rightContent={
                props.profileId && !props.omitChatButton ? (
                    <AIChatButton medicalProfileId={props.profileId} useNestedNavigation={false} />
                ) : null
            }
        />
    );
};

const ProfileHeader: React.FC<ProfileHeaderProps> = (props) => {
    const profileId = (props.route.params as any)?.profileId || (props.route.params as any)?.medicalProfileId || null;

    if (!profileId) {
        console.error('ProfileHeader: profileId is required');
    }
    useRedirectOnInvalidParams(!profileId);

    if (!profileId) return null;

    return <ProfileHeaderBase profileId={profileId} {...props} />;
};

const ProfileNoteHeader: React.FC<ProfileHeaderProps> = (props) => {
    const noteId = (props.route.params as any)?.noteId || null;
    if (!noteId) {
        console.error('ProfileNoteHeader: noteId is required');
    }

    const { isLoadingNote, note } = useNotes({
        noteId,
    });

    if (note && !note.medicalProfileId) {
        console.error('ProfileNoteHeader: note.medicalProfileId is required');
    }

    const isInvalid = !noteId || (!isLoadingNote && !note?.medicalProfileId);
    useRedirectOnInvalidParams(isInvalid);

    if (isInvalid) return null;

    if (isLoadingNote) {
        return <ProfileHeaderBase isInitializing profileId={null} {...props} />;
    } else {
        return <ProfileHeaderBase profileId={note!.medicalProfileId!} {...props} />;
    }
};

export { ProfileHeader, ProfileNoteHeader };
